"use client";

import { useCallback, useRef } from "react";
import { useRouter } from "next/navigation";
import { useQueryClient } from "@tanstack/react-query";
import { useSnapshot } from "valtio";
import { useNotificationPermission } from "@hooks/useNotificationPermission";
import { openPlaidConnectFlowModal } from "@components/PlaidConnectFlow/openPlaidConnectFlowModal";
import { ROUTER_PATH } from "@config/router";
import { useOspApiClientProvider } from "@hooks/useOspApiClientProvider";
import { usePlanGoalChatOpen } from "@/src/hooks/plan/usePlanGoalChatOpen";
import { fetchPlanPendingGoals } from "@/src/hooks/plan/fetchPlanDiagnosisGoals";
import { planQueryKeys } from "@/src/hooks/plan/planQueryKeys";
import { readActivePendingGoalSkipIdSet } from "@/src/hooks/plan/planPendingGoalSkipStorage";
import { rootStore } from "@store/rootStore";
import { buildGoalDetailPath } from "@utils/goalRoute";
import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/features/onboarding-checklist/hooks/useSetupChecklistDispatcher";
import {
  SetupChecklistStore,
  fetchChecklist,
  handleNotificationGranted,
} from "@/src/store/modules/setupChecklist";
import {
  handleChecklistAction,
  type ChecklistActionHandlers,
} from "../utils/actionRouter";
import { resolveActivateGoalTarget } from "../utils/resolveActivateGoalTarget";
import { isWebNotificationBlocked } from "../utils/notificationGate";
import { markPendingPushSettingsReturn } from "../utils/pushSettingsReturn";
import {
  getLastPushEntry,
  trackConnectPageView,
  trackGoalActivateEntryView,
  trackPlaidConnectSuccess,
  trackPushPermissionDenied,
  trackPushPermissionGranted,
  trackPushSettingsRedirect,
  trackPushSystemPromptView,
  type ConnectAccountType,
} from "../utils/tracking";
import type { ChecklistStepKey } from "../types";

const I18n = I18nUtil.createScoped(Translations);

const isStepCompleted = (stepKey: ChecklistStepKey): boolean =>
  !!SetupChecklistStore.data?.steps.find((s) => s.stepKey === stepKey)
    ?.completed;

/**
 * 清单动作统一调度 Hook
 * 连接 router、原生通知授权与 Plaid 连接 Flow
 */
export function useSetupChecklistDispatcher() {
  const router = useRouter();
  const { requestNotificationPermission } = useNotificationPermission();
  const queryClient = useQueryClient();
  const { trackerApi } = useOspApiClientProvider();
  const { ownerInfo } = useSnapshot(rootStore.user);
  const { openGoalCreatorChat } = usePlanGoalChatOpen();
  const goalActivationInFlightRef = useRef(false);
  // 授权 + 原生上报（bind / setting）进行中时忽略重复点击，避免同一时刻并发多次 /setting
  const notificationInFlightRef = useRef(false);

  // Plaid Flow 只返回「已关闭」，没有成功与否：关闭后强制刷新清单，对应项由未完成变为已完成即视为连接成功
  const openPlaidAndRefresh = async (
    accountType: ConnectAccountType,
    stepKey: ChecklistStepKey,
  ) => {
    trackConnectPageView(accountType);
    const wasCompleted = isStepCompleted(stepKey);
    await openPlaidConnectFlowModal({
      accountType: accountType === "banking" ? "bank" : "invest",
    });
    await fetchChecklist(true);
    if (!wasCompleted && isStepCompleted(stepKey)) {
      trackPlaidConnectSuccess(accountType);
    }
  };

  const handlers: ChecklistActionHandlers = {
    navigateToFitnessScore: () => {
      // app 内健康分页 = /my（MyScoreTab）。"/financial-fitness" 是官网营销页
      // rewrite（rewrites.config.mjs → homesite），会把用户带离产品壳
      router.push(ROUTER_PATH.MY_SCORE);
    },
    requestNotification: async () => {
      // 纯 web 没有授权通道（桥请求会等到 30s 超时），入口已隐藏按钮，这里兜底
      if (
        notificationInFlightRef.current ||
        isWebNotificationBlocked("notification")
      ) {
        return;
      }
      notificationInFlightRef.current = true;
      const entry = getLastPushEntry();
      try {
        const result = await requestNotificationPermission();
        if (result?.enabled) {
          // 仅授权成功才乐观完成 + 刷新清单；拒绝 / 跳系统设置页不触发
          trackPushSystemPromptView(entry);
          trackPushPermissionGranted(entry, false);
          await handleNotificationGranted();
        } else if (result?.action === "open-settings") {
          // 已拒绝过、系统弹窗无法再唤起 → 跳系统设置页；回到 App 后由 Global Manager 核对结果
          trackPushSettingsRedirect(entry);
          markPendingPushSettingsReturn(entry);
        } else {
          trackPushSystemPromptView(entry);
          trackPushPermissionDenied(entry);
        }
      } catch {
        // 权限被拒或请求取消
      } finally {
        notificationInFlightRef.current = false;
      }
    },
    // Plaid Flow 关闭（成功 / 取消）后刷新清单：连接走原生 Plaid SDK 浮层，WebView 不触发
    // visibilitychange，不主动拉取则清单停留在连接前的状态（beta 实测：后端已 5/6，页面仍旧值）。
    // 不在这里串联 PostConnectNotificationPrompt —— App 内连接成功后原生 account_read_pending
    // 已带推送引导，再弹 web 引导会叠框。
    openPlaidInvest: () => {
      void openPlaidAndRefresh("invest", "invest_account");
    },
    openPlaidBanking: () => {
      void openPlaidAndRefresh("banking", "banking_account");
    },
    // PRD S1.9：进建 tracker 方向选择整页；选方向后带预置问法进 Chat 由 Ed 追问细节再创建
    // （方向选择与进 chat 在 BuildTrackerPage 里）。返回落回清单页。
    openBuildTracker: () => {
      router.push(ROUTER_PATH.BUILD_TRACKER);
    },
    // 按待激活 goal 数分流（Figma Activate goal 分支图）：0 → Chat 新建；1 → 该 goal 的
    // Plan overview；多个 → 选择页。点击时现拉一次（staleTime 0）—— 清单页不常驻 goal 列表，
    // 缓存可能是连接账户前的旧数。拉取失败落选择页，由页面自带的错误重试兜底。
    navigateToGoalActivation: async () => {
      if (goalActivationInFlightRef.current) {
        return;
      }
      goalActivationInFlightRef.current = true;
      try {
        const ownerId = ownerInfo?.id ?? "";
        // client 未就绪时 fetchPlanPendingGoals 返回 []，会被误判成「没有 goal」→ 直接落选择页
        if (!trackerApi || !ownerId) {
          router.push(ROUTER_PATH.ACTIVATE_GOAL);
          return;
        }
        const target = await queryClient
          .fetchQuery({
            queryKey: planQueryKeys.pendingGoals(ownerId),
            queryFn: () => fetchPlanPendingGoals(trackerApi),
            staleTime: 0,
          })
          .then((goals) =>
            resolveActivateGoalTarget(
              goals,
              ownerId ? readActivePendingGoalSkipIdSet(ownerId) : new Set(),
            ),
          )
          .catch(() => ({ kind: "select" as const, pendingCount: 0 }));
        if (target.kind === "create") {
          trackGoalActivateEntryView("none");
          openGoalCreatorChat(I18n.t("I want to set a new goal"));
        } else if (target.kind === "detail") {
          trackGoalActivateEntryView(target.refined ? "refined" : "unrefined");
          router.push(buildGoalDetailPath(target.goalId));
        } else {
          // 选择页（state=multiple）的曝光由页面自己上报，带 pending_count
          router.push(ROUTER_PATH.ACTIVATE_GOAL);
        }
      } finally {
        goalActivationInFlightRef.current = false;
      }
    },
  };

  const dispatch = useCallback(
    (stepKey: ChecklistStepKey) => {
      handleChecklistAction(stepKey, handlers);
    },
    [router, requestNotificationPermission, openGoalCreatorChat, trackerApi],
  );

  return { dispatch, handlers };
}
